import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import GdprPanel from '../components/GdprPanel';
import ArrivalWindowSettings from '../components/ArrivalWindowSettings';
import PhotoDropzone from '../components/PhotoDropzone';
import {
  adminListUsers,
  adminCreateUser,
  adminUpdateUser,
  adminDeleteUser,
  adminListChecklistTemplates,
  adminCreateChecklistTemplate,
  adminUpdateChecklistTemplate,
  adminDeleteChecklistTemplate,
  adminListStepTemplates,
  adminCreateStepTemplate,
  adminUpdateStepTemplate,
  adminDeleteStepTemplate,
  adminGetSettings,
  adminUpdateSettings,
  adminUploadLogo,
  adminUploadTerms,
  adminDownloadTerms,
  adminListPresets,
  adminCreatePreset,
  adminUpdatePreset,
  adminDeletePreset,
  adminTestInvoiceNinja,
  adminTestAi,
  adminListAiModels,
  adminAiCostEstimate,
} from '../services/api';
import AiUsagePanel from '../components/AiUsagePanel';
import { apiErrorMessage } from '../utils/apiError';
import { openPdfInNewTab } from '../utils/openPdf';

const AI_KEY_LABEL = { gemini: 'aiKeyGemini', openai: 'aiKeyOpenai', anthropic: 'aiKeyAnthropic' };
// Tier -> [in-price setting key, out-price setting key, i18n tier label]
const AI_PRICE_TIERS = [
  ['economy', 'aiTierEconomy'],
  ['standard', 'aiTierStandard'],
  ['vision_fast', 'aiTierVisionFast'],
  ['vision_premium', 'aiTierVisionPremium'],
];

export default function AdminPage() {
  const { t } = useTranslation();
  // --- Users state ---
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [newRole, setNewRole] = useState('reporter');
  const [error, setError] = useState('');

  // --- Checklist Templates state ---
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [templateForm, setTemplateForm] = useState({ label: '', report_text: '', order: 0 });
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const [templateError, setTemplateError] = useState('');

  // --- Step Templates state ---
  const [stepTemplates, setStepTemplates] = useState([]);
  const [stepTemplatesLoading, setStepTemplatesLoading] = useState(true);
  const [showStepForm, setShowStepForm] = useState(false);
  const [stepForm, setStepForm] = useState({ title: '', order: 0, kind: 'text' });
  const [editingStepId, setEditingStepId] = useState(null);
  const [stepError, setStepError] = useState('');

  // --- PDF Settings state ---
  const [pdfSettings, setPdfSettings] = useState({});
  const [pdfSettingsLoading, setPdfSettingsLoading] = useState(true);
  const [pdfSaving, setPdfSaving] = useState(false);
  const [pdfSaved, setPdfSaved] = useState(false);
  const [logoError, setLogoError] = useState(null);
  const [termsError, setTermsError] = useState(null);
  const [termsNotice, setTermsNotice] = useState(null);
  const [bccError, setBccError] = useState(null);

  // --- InvoiceNinja state ---
  const [inToken, setInToken] = useState('');
  const [inSaving, setInSaving] = useState(false);
  const [inSaved, setInSaved] = useState(false);
  const [inTesting, setInTesting] = useState(false);
  const [inTestResult, setInTestResult] = useState(null);

  // --- AI state ---
  const [aiKeys, setAiKeys] = useState({ gemini: '', openai: '', anthropic: '' });
  const [aiSaving, setAiSaving] = useState(false);
  const [aiSaved, setAiSaved] = useState(false);
  const [aiTesting, setAiTesting] = useState(false);
  const [aiTestResult, setAiTestResult] = useState(null);
  const [aiModels, setAiModels] = useState(null);   // {ok, models[], detail}
  const [aiModelsLoading, setAiModelsLoading] = useState(false);
  // Cost estimator
  const [aiEstimate, setAiEstimate] = useState(null);
  const [aiEstChars, setAiEstChars] = useState(8000);
  const [aiEstImages, setAiEstImages] = useState(8);
  const [aiEstTick, setAiEstTick] = useState(0);  // bump to refetch after save

  // --- Billing presets state ---
  const [presets, setPresets] = useState([]);
  const [presetsLoading, setPresetsLoading] = useState(true);
  const [showPresetForm, setShowPresetForm] = useState(false);
  const [presetForm, setPresetForm] = useState({
    label: '', description: '', default_price: '0', default_tax_rate: '21',
    default_tax_name: 'TVA', active: true, order: 0,
  });
  const [editingPresetId, setEditingPresetId] = useState(null);
  const [presetError, setPresetError] = useState('');

  // --- Active tab ---
  const [activeTab, setActiveTab] = useState('users');

  useEffect(() => {
    adminListUsers()
      .then(setUsers)
      .catch(() => {})
      .finally(() => setLoading(false));
    adminListChecklistTemplates()
      .then(setTemplates)
      .catch(() => {})
      .finally(() => setTemplatesLoading(false));
    adminListStepTemplates()
      .then(setStepTemplates)
      .catch(() => {})
      .finally(() => setStepTemplatesLoading(false));
    adminGetSettings()
      .then(setPdfSettings)
      .catch(() => {})
      .finally(() => setPdfSettingsLoading(false));
    adminListPresets()
      .then(setPresets)
      // Swallowed, a failed load showed "no presets" — with nothing to delete.
      .catch((err) => setPresetError(apiErrorMessage(err, t)))
      .finally(() => setPresetsLoading(false));
  }, []);

  // Load the cost estimate when the AI tab is open (debounced on assumptions).
  useEffect(() => {
    if (activeTab !== 'ai') return undefined;
    let cancelled = false;
    const timer = setTimeout(() => {
      adminAiCostEstimate({ avgChars: aiEstChars, avgImages: aiEstImages })
        .then((d) => { if (!cancelled) setAiEstimate(d); })
        .catch(() => { if (!cancelled) setAiEstimate(null); });
    }, 350);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [activeTab, aiEstChars, aiEstImages, aiEstTick]);

  if (loading) return <p style={{ textAlign: 'center', paddingTop: 40 }}>{t('auth.loading')}</p>;

  // --- User handlers ---
  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    if (!newEmail.trim()) {
      setError(t('admin.emailRequired'));
      return;
    }
    try {
      const user = await adminCreateUser({
        email: newEmail.trim(),
        full_name: newName.trim(),
        role: newRole,
      });
      setUsers((prev) => [user, ...prev]);
      setNewEmail('');
      setNewName('');
      setNewRole('reporter');
      setShowForm(false);
    } catch (err) {
      if (err.response?.status === 409) {
        setError(t('errors.duplicateEmail'));
      } else {
        setError(t('errors.generic'));
      }
    }
  };

  const handleChangeRole = async (user, role) => {
    const updated = await adminUpdateUser(user.id, { role });
    setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
  };

  const handleToggleActive = async (user) => {
    const updated = await adminUpdateUser(user.id, { is_active: !user.is_active });
    setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
  };

  const handleDelete = async (userId) => {
    if (!window.confirm(t('admin.deleteConfirm'))) return;
    await adminDeleteUser(userId);
    setUsers((prev) => prev.filter((u) => u.id !== userId));
  };

  // --- Template handlers ---
  const resetTemplateForm = () => {
    setTemplateForm({ label: '', report_text: '', order: 0 });
    setEditingTemplateId(null);
    setShowTemplateForm(false);
    setTemplateError('');
  };

  const handleTemplateSubmit = async (e) => {
    e.preventDefault();
    setTemplateError('');
    if (!templateForm.label.trim()) {
      setTemplateError(t('admin.templateLabelRequired'));
      return;
    }
    try {
      if (editingTemplateId) {
        const updated = await adminUpdateChecklistTemplate(editingTemplateId, templateForm);
        setTemplates((prev) => prev.map((tmpl) => (tmpl.id === updated.id ? updated : tmpl)));
      } else {
        const created = await adminCreateChecklistTemplate(templateForm);
        setTemplates((prev) => [...prev, created]);
      }
      resetTemplateForm();
    } catch (err) {
      setTemplateError(apiErrorMessage(err, t));
    }
  };

  const handleEditTemplate = (tmpl) => {
    setTemplateForm({ label: tmpl.label, report_text: tmpl.report_text || '', order: tmpl.order });
    setEditingTemplateId(tmpl.id);
    setShowTemplateForm(true);
    setTemplateError('');
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm(t('admin.templateDeleteConfirm'))) return;
    setTemplateError('');
    try {
      await adminDeleteChecklistTemplate(templateId);
      setTemplates((prev) => prev.filter((tmpl) => tmpl.id !== templateId));
    } catch (err) {
      setTemplateError(apiErrorMessage(err, t));
    }
  };

  // --- Step Template handlers ---
  const resetStepForm = () => {
    setStepForm({ title: '', order: 0, kind: 'text' });
    setEditingStepId(null);
    setShowStepForm(false);
    setStepError('');
  };

  const handleStepSubmit = async (e) => {
    e.preventDefault();
    setStepError('');
    if (!stepForm.title.trim()) {
      setStepError(t('admin.stepTitleRequired'));
      return;
    }
    try {
      if (editingStepId) {
        const updated = await adminUpdateStepTemplate(editingStepId, stepForm);
        setStepTemplates((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
      } else {
        const created = await adminCreateStepTemplate(stepForm);
        setStepTemplates((prev) => [...prev, created]);
      }
      resetStepForm();
    } catch (err) {
      setStepError(apiErrorMessage(err, t));
    }
  };

  const handleEditStep = (step) => {
    setStepForm({ title: step.title, order: step.order, kind: step.kind || 'text' });
    setEditingStepId(step.id);
    setShowStepForm(true);
    setStepError('');
  };

  const handleDeleteStep = async (stepId) => {
    if (!window.confirm(t('admin.stepDeleteConfirm'))) return;
    setStepError('');
    try {
      await adminDeleteStepTemplate(stepId);
      setStepTemplates((prev) => prev.filter((s) => s.id !== stepId));
    } catch (err) {
      setStepError(apiErrorMessage(err, t));
    }
  };

  // --- PDF Settings handlers ---
  const handlePdfSettingsChange = (key, value) => {
    setPdfSettings((prev) => ({ ...prev, [key]: value }));
    setPdfSaved(false);
  };

  const handleSavePdfSettings = async () => {
    setBccError(null);
    // Checked here too so a typo does not cost a round trip; the server stays the authority.
    if (pdfSettings.email_bcc_enabled === 'true' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((pdfSettings.email_bcc_address || '').trim())) {
      setBccError(t('admin.emailBccInvalid'));
      return;
    }
    setPdfSaving(true);
    try {
      const updated = await adminUpdateSettings(pdfSettings);
      setPdfSettings(updated);
      setPdfSaved(true);
      setTimeout(() => setPdfSaved(false), 2000);
    } catch (err) {
      // A 400 here is the server refusing the copy address; other failures stay silent as before.
      if (err?.response?.status === 400) setBccError(apiErrorMessage(err, t));
    } finally {
      setPdfSaving(false);
    }
  };

  const handleLogoUpload = async (files) => {
    const file = files?.[0];
    if (!file) return;
    setLogoError(null);
    try {
      const result = await adminUploadLogo(file);
      setPdfSettings((prev) => ({ ...prev, pdf_logo_filename: result.filename }));
      setPdfSaved(false);
    } catch {
      // The server only takes PNG/JPEG/SVG. Swallowing this left an admin
      // staring at an unchanged logo with no idea why.
      setLogoError(t('admin.pdfLogoRejected'));
    }
  };

  // The terms PDF is stored on upload (not on "Save"), so the settings state
  // only mirrors the new version/date for display.
  const handleTermsUpload = async (files) => {
    const file = files?.[0];
    if (!file) return;
    setTermsError(null);
    setTermsNotice(null);
    try {
      const result = await adminUploadTerms(file);
      setPdfSettings((prev) => ({
        ...prev,
        terms_pdf_filename: result.filename,
        terms_version: result.version,
        terms_uploaded_at: result.uploaded_at,
      }));
      setTermsNotice(t('admin.termsUploaded', { version: result.version }));
    } catch (err) {
      // Non-PDF, empty or over 10 MB: the server says which, so show it.
      setTermsError(apiErrorMessage(err, t));
    }
  };

  const handleTermsView = async () => {
    setTermsError(null);
    try {
      await openPdfInNewTab(adminDownloadTerms, pdfSettings.terms_pdf_filename || 'cgv.pdf');
    } catch {
      setTermsError(t('admin.termsViewFailed'));
    }
  };

  // --- InvoiceNinja handlers ---
  const handleSaveInvoiceNinja = async () => {
    setInSaving(true);
    setInTestResult(null);
    try {
      const payload = {
        invoiceninja_base_url: pdfSettings.invoiceninja_base_url || '',
        invoiceninja_currency_id: pdfSettings.invoiceninja_currency_id || '',
        invoiceninja_country_id: pdfSettings.invoiceninja_country_id || '250',
        invoiceninja_default_tax_rate: pdfSettings.invoiceninja_default_tax_rate || '20',
        invoiceninja_default_tax_name: pdfSettings.invoiceninja_default_tax_name || 'TVA',
        invoiceninja_timezone: pdfSettings.invoiceninja_timezone || 'Europe/Paris',
        invoiceninja_auto_send: String(pdfSettings.invoiceninja_auto_send) === 'true' ? 'true' : 'false',
        invoiceninja_due_days: String(pdfSettings.invoiceninja_due_days ?? '7'),
      };
      if (inToken.trim()) payload.invoiceninja_api_token = inToken.trim();
      const updated = await adminUpdateSettings(payload);
      setPdfSettings(updated);
      setInToken('');
      setInSaved(true);
      setTimeout(() => setInSaved(false), 2000);
    } catch {
      // error
    } finally {
      setInSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setInTesting(true);
    setInTestResult(null);
    try {
      const res = await adminTestInvoiceNinja({
        base_url: pdfSettings.invoiceninja_base_url || '',
        token: inToken.trim() || undefined,
      });
      setInTestResult(res);
    } catch {
      setInTestResult({ ok: false, reason: 'error' });
    } finally {
      setInTesting(false);
    }
  };

  // --- AI handlers ---
  const handleSaveAi = async () => {
    setAiSaving(true);
    setAiTestResult(null);
    try {
      const payload = {
        ai_enabled: String(pdfSettings.ai_enabled) === 'true' ? 'true' : 'false',
        ai_provider: pdfSettings.ai_provider || 'gemini',
        ai_timeout: String(pdfSettings.ai_timeout || '60'),
        ai_model_economy: pdfSettings.ai_model_economy || '',
        ai_model_standard: pdfSettings.ai_model_standard || '',
        ai_model_vision_fast: pdfSettings.ai_model_vision_fast || '',
        ai_model_vision_premium: pdfSettings.ai_model_vision_premium || '',
        ai_default_level: String(pdfSettings.ai_default_level || '1'),
        ai_max_level: String(pdfSettings.ai_max_level || '4'),
      };
      AI_PRICE_TIERS.forEach(([tier]) => {
        payload[`ai_price_${tier}_in`] = String(pdfSettings[`ai_price_${tier}_in`] ?? '');
        payload[`ai_price_${tier}_out`] = String(pdfSettings[`ai_price_${tier}_out`] ?? '');
      });
      if (aiKeys.gemini.trim()) payload.ai_gemini_api_key = aiKeys.gemini.trim();
      if (aiKeys.openai.trim()) payload.ai_openai_api_key = aiKeys.openai.trim();
      if (aiKeys.anthropic.trim()) payload.ai_anthropic_api_key = aiKeys.anthropic.trim();
      const updated = await adminUpdateSettings(payload);
      setPdfSettings(updated);
      setAiKeys({ gemini: '', openai: '', anthropic: '' });
      setAiSaved(true);
      setAiEstTick((n) => n + 1);  // refresh estimate with saved provider/prices
      setTimeout(() => setAiSaved(false), 2000);
    } catch {
      // error
    } finally {
      setAiSaving(false);
    }
  };

  const handleTestAi = async () => {
    const provider = pdfSettings.ai_provider || 'gemini';
    setAiTesting(true);
    setAiTestResult(null);
    try {
      const res = await adminTestAi({
        provider,
        api_key: (aiKeys[provider] || '').trim() || undefined,
        model: pdfSettings.ai_model_economy || undefined,
      });
      setAiTestResult(res);
    } catch {
      setAiTestResult({ ok: false, reason: 'error' });
    } finally {
      setAiTesting(false);
    }
  };

  // Model names change often; let the admin read the provider's live catalogue
  // instead of guessing which default still exists.
  const handleListAiModels = async () => {
    const provider = pdfSettings.ai_provider || 'gemini';
    setAiModelsLoading(true);
    setAiModels(null);
    try {
      setAiModels(await adminListAiModels({
        provider,
        api_key: (aiKeys[provider] || '').trim() || undefined,
      }));
    } catch {
      setAiModels({ ok: false, reason: 'error', models: [] });
    } finally {
      setAiModelsLoading(false);
    }
  };

  // --- Billing preset handlers ---
  const resetPresetForm = () => {
    setPresetForm({ label: '', description: '', default_price: '0', default_tax_rate: '21', default_tax_name: 'TVA', active: true, order: 0 });
    setEditingPresetId(null);
    setShowPresetForm(false);
    setPresetError('');
  };

  const handlePresetSubmit = async (e) => {
    e.preventDefault();
    setPresetError('');
    if (!presetForm.label.trim()) {
      setPresetError(t('admin.presetLabelRequired'));
      return;
    }
    try {
      if (editingPresetId) {
        const updated = await adminUpdatePreset(editingPresetId, presetForm);
        setPresets((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      } else {
        const created = await adminCreatePreset(presetForm);
        setPresets((prev) => [...prev, created]);
      }
      resetPresetForm();
    } catch (err) {
      setPresetError(apiErrorMessage(err, t));
    }
  };

  const handleEditPreset = (p) => {
    setPresetForm({
      label: p.label, description: p.description || '',
      default_price: String(p.default_price), default_tax_rate: String(p.default_tax_rate),
      default_tax_name: p.default_tax_name || 'TVA', active: p.active, order: p.order,
    });
    setEditingPresetId(p.id);
    setShowPresetForm(true);
    setPresetError('');
  };

  const handleDeletePreset = async (id) => {
    if (!window.confirm(t('admin.presetDeleteConfirm'))) return;
    setPresetError('');
    try {
      await adminDeletePreset(id);
      setPresets((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      setPresetError(apiErrorMessage(err, t));
    }
  };

  // Retiring a preset without deleting it: it leaves the invoice editor's
  // list but stays on the invoices that used it.
  const handleTogglePreset = async (p) => {
    setPresetError('');
    try {
      const updated = await adminUpdatePreset(p.id, { active: !p.active });
      setPresets((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
    } catch (err) {
      setPresetError(apiErrorMessage(err, t));
    }
  };

  return (
    <div className="fade-in">
      <div className="page-header">
        <h1 className="page-title">{t('admin.title')}</h1>
      </div>

      {/* Tabs */}
      <div className="tabs">
        <button
          className={`tab-btn ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          {t('admin.users')}
        </button>
        <button
          className={`tab-btn ${activeTab === 'checklist' ? 'active' : ''}`}
          onClick={() => setActiveTab('checklist')}
        >
          {t('admin.checklistTemplates')}
        </button>
        <button
          className={`tab-btn ${activeTab === 'steps' ? 'active' : ''}`}
          onClick={() => setActiveTab('steps')}
        >
          {t('admin.stepTemplates')}
        </button>
        <button
          className={`tab-btn ${activeTab === 'pdf' ? 'active' : ''}`}
          onClick={() => setActiveTab('pdf')}
        >
          {t('admin.pdfSettings')}
        </button>
        <button
          className={`tab-btn ${activeTab === 'invoiceninja' ? 'active' : ''}`}
          onClick={() => setActiveTab('invoiceninja')}
        >
          {t('admin.invoiceNinja')}
        </button>
        <button
          className={`tab-btn ${activeTab === 'presets' ? 'active' : ''}`}
          onClick={() => setActiveTab('presets')}
        >
          {t('admin.presets')}
        </button>
        <button
          className={`tab-btn ${activeTab === 'ai' ? 'active' : ''}`}
          onClick={() => setActiveTab('ai')}
        >
          {t('admin.ai')}
        </button>
        <button
          className={`tab-btn ${activeTab === 'gdpr' ? 'active' : ''}`}
          onClick={() => setActiveTab('gdpr')}
        >
          {t('gdpr.tab')}
        </button>
      </div>

      {/* Users Tab */}
      {activeTab === 'users' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>
              + {t('admin.addUser')}
            </button>
          </div>

          {showForm && (
            <div className="card" style={{ marginBottom: 24 }}>
              <h3 style={{ marginBottom: 16, fontSize: 16, fontWeight: 700 }}>{t('admin.addUser')}</h3>
              <form onSubmit={handleCreate}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div className="form-group">
                    <label>{t('admin.email')} *</label>
                    <input
                      className="form-control"
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('admin.fullName')}</label>
                    <input
                      className="form-control"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>{t('admin.role')}</label>
                  <select
                    className="form-control"
                    value={newRole}
                    onChange={(e) => setNewRole(e.target.value)}
                    style={{ width: 200 }}
                  >
                    <option value="reporter">{t('admin.roleReporter')}</option>
                    <option value="admin">{t('admin.roleAdmin')}</option>
                    <option value="viewer">{t('admin.roleViewer')}</option>
                  </select>
                </div>
                {error && (
                  <p style={{ color: 'var(--color-danger)', marginBottom: 12, fontSize: 13, fontWeight: 500 }}>{error}</p>
                )}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-primary" type="submit">{t('admin.addUser')}</button>
                  <button className="btn btn-secondary" type="button" onClick={() => setShowForm(false)}>{t('wizard.cancel')}</button>
                </div>
              </form>
            </div>
          )}

          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t('admin.fullName')}</th>
                    <th>{t('admin.email')}</th>
                    <th>{t('admin.role')}</th>
                    <th>{t('admin.status')}</th>
                    <th>{t('admin.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td style={{ fontWeight: 600 }}>{user.full_name || '\u2014'}</td>
                      <td style={{ fontSize: 13, color: 'var(--color-text-light)' }}>{user.email}</td>
                      <td>
                        <select
                          className="form-control"
                          value={user.role}
                          onChange={(e) => handleChangeRole(user, e.target.value)}
                          style={{ width: 130, fontSize: 13, padding: '4px 8px' }}
                        >
                          <option value="reporter">{t('admin.roleReporter')}</option>
                          <option value="admin">{t('admin.roleAdmin')}</option>
                          <option value="viewer">{t('admin.roleViewer')}</option>
                        </select>
                      </td>
                      <td>
                        <span className={`badge ${user.is_active ? 'badge-completed' : 'badge-draft'}`}>
                          {user.is_active ? t('admin.active') : t('admin.inactive')}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => handleToggleActive(user)}
                          >
                            {user.is_active ? t('admin.deactivate') : t('admin.activate')}
                          </button>
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => handleDelete(user.id)}
                          >
                            {t('admin.deleteUser')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Checklist Templates Tab */}
      {activeTab === 'checklist' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <button className="btn btn-primary" onClick={() => { resetTemplateForm(); setShowTemplateForm(true); }}>
              + {t('admin.addChecklistItem')}
            </button>
          </div>

          {templateError && !showTemplateForm && (
            <p className="admin-error" role="alert">{templateError}</p>
          )}
          {showTemplateForm && (
            <div className="card" style={{ marginBottom: 24 }}>
              <h3 style={{ marginBottom: 16, fontSize: 16, fontWeight: 700 }}>
                {editingTemplateId ? t('admin.editChecklistItem') : t('admin.addChecklistItem')}
              </h3>
              <form onSubmit={handleTemplateSubmit}>
                {/* The internal key is derived from the label server-side: the
                    expert only names the check point once. */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 16 }}>
                  <div className="form-group">
                    <label>{t('admin.templateLabel')} *</label>
                    <input
                      className="form-control"
                      value={templateForm.label}
                      onChange={(e) => setTemplateForm({ ...templateForm, label: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>{t('admin.templateOrder')}</label>
                  <input
                    className="form-control"
                    type="number"
                    value={templateForm.order}
                    onChange={(e) => setTemplateForm({ ...templateForm, order: parseInt(e.target.value) || 0 })}
                    style={{ width: 120 }}
                  />
                </div>
                <div className="form-group">
                  <label>{t('admin.reportText')}</label>
                  <textarea
                    className="form-control"
                    value={templateForm.report_text}
                    onChange={(e) => setTemplateForm({ ...templateForm, report_text: e.target.value })}
                    placeholder={t('admin.reportTextPlaceholder')}
                    rows={4}
                  />
                  <small style={{ color: 'var(--color-text-light)', fontSize: 12, marginTop: 4, display: 'block' }}>
                    {t('admin.reportTextHelp')}
                  </small>
                </div>
                {templateError && (
                  <p style={{ color: 'var(--color-danger)', marginBottom: 12, fontSize: 13, fontWeight: 500 }}>{templateError}</p>
                )}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-primary" type="submit">
                    {editingTemplateId ? t('wizard.save') : t('admin.addChecklistItem')}
                  </button>
                  <button className="btn btn-secondary" type="button" onClick={resetTemplateForm}>
                    {t('wizard.cancel')}
                  </button>
                </div>
              </form>
            </div>
          )}

          {templatesLoading ? (
            <p>{t('auth.loading')}</p>
          ) : templates.length === 0 ? (
            <div className="card empty-state">
              <div className="empty-state-icon">&#9776;</div>
              <p>{t('admin.noTemplates')}</p>
            </div>
          ) : (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto' }}>
                <table className="data-table stack-mobile">
                  <thead>
                    <tr>
                      <th>{t('admin.templateOrder')}</th>
                      <th>{t('admin.templateLabel')}</th>
                      <th>{t('admin.reportText')}</th>
                      <th>{t('admin.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {templates.map((tmpl) => (
                      <tr key={tmpl.id}>
                        <td data-label={t('admin.templateOrder')} style={{ fontWeight: 600, color: 'var(--color-text-light)' }}>{tmpl.order}</td>
                        <td style={{ fontWeight: 500 }}>{tmpl.label}</td>
                        <td data-label={t('admin.reportText')} style={{ maxWidth: 300 }}>
                          {tmpl.report_text ? (
                            <span style={{ fontSize: 13, color: 'var(--color-text-light)', whiteSpace: 'pre-wrap' }}>
                              {tmpl.report_text.length > 100 ? tmpl.report_text.substring(0, 100) + '\u2026' : tmpl.report_text}
                            </span>
                          ) : (
                            <span style={{ color: 'var(--color-text-lighter)', fontStyle: 'italic', fontSize: 13 }}>\u2014</span>
                          )}
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button className="btn btn-secondary btn-sm" onClick={() => handleEditTemplate(tmpl)}>
                              {t('admin.edit')}
                            </button>
                            <button className="btn btn-danger btn-sm" onClick={() => handleDeleteTemplate(tmpl.id)}>
                              {t('admin.deleteUser')}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Step Templates Tab */}
      {activeTab === 'steps' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <button className="btn btn-primary" onClick={() => { resetStepForm(); setShowStepForm(true); }}>
              + {t('admin.addStepTemplate')}
            </button>
          </div>

          {stepError && !showStepForm && (
            <p className="admin-error" role="alert">{stepError}</p>
          )}
          {showStepForm && (
            <div className="card" style={{ marginBottom: 24 }}>
              <h3 style={{ marginBottom: 16, fontSize: 16, fontWeight: 700 }}>
                {editingStepId ? t('admin.editStepTemplate') : t('admin.addStepTemplate')}
              </h3>
              <form onSubmit={handleStepSubmit}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16 }}>
                  <div className="form-group">
                    <label>{t('admin.stepTitle')} *</label>
                    <input
                      className="form-control"
                      value={stepForm.title}
                      onChange={(e) => setStepForm({ ...stepForm, title: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('admin.templateOrder')}</label>
                    <input
                      className="form-control"
                      type="number"
                      value={stepForm.order}
                      onChange={(e) => setStepForm({ ...stepForm, order: parseInt(e.target.value) || 0 })}
                      style={{ width: 120 }}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="step-kind">{t('admin.stepKind')}</label>
                    <select
                      id="step-kind"
                      className="form-control"
                      value={stepForm.kind}
                      onChange={(e) => setStepForm({ ...stepForm, kind: e.target.value })}
                    >
                      <option value="text">{t('admin.stepKindText')}</option>
                      <option value="photos">{t('admin.stepKindPhotos')}</option>
                    </select>
                  </div>
                </div>
                {stepError && (
                  <p style={{ color: 'var(--color-danger)', marginBottom: 12, fontSize: 13, fontWeight: 500 }}>{stepError}</p>
                )}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-primary" type="submit">
                    {editingStepId ? t('wizard.save') : t('admin.addStepTemplate')}
                  </button>
                  <button className="btn btn-secondary" type="button" onClick={resetStepForm}>
                    {t('wizard.cancel')}
                  </button>
                </div>
              </form>
            </div>
          )}

          {stepTemplatesLoading ? (
            <p>{t('auth.loading')}</p>
          ) : stepTemplates.length === 0 ? (
            <div className="card empty-state">
              <div className="empty-state-icon">&#9776;</div>
              <p>{t('admin.noStepTemplates')}</p>
            </div>
          ) : (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto' }}>
                <table className="data-table stack-mobile">
                  <thead>
                    <tr>
                      <th>{t('admin.templateOrder')}</th>
                      <th>{t('admin.stepTitle')}</th>
                      <th>{t('admin.stepKind')}</th>
                      <th>{t('admin.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stepTemplates.map((step) => (
                      <tr key={step.id}>
                        <td data-label={t('admin.templateOrder')} style={{ fontWeight: 600, color: 'var(--color-text-light)' }}>{step.order}</td>
                        <td style={{ fontWeight: 500 }}>{step.title}</td>
                        <td data-label={t('admin.stepKind')}>{step.kind === 'photos' ? t('admin.stepKindPhotos') : t('admin.stepKindText')}</td>
                        <td>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button className="btn btn-secondary btn-sm" onClick={() => handleEditStep(step)}>
                              {t('admin.edit')}
                            </button>
                            <button className="btn btn-danger btn-sm" onClick={() => handleDeleteStep(step.id)}>
                              {t('admin.deleteUser')}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* PDF Settings Tab */}
      {activeTab === 'pdf' && (
        <div className="card" style={{ marginTop: 0 }}>
          <h3 style={{ marginBottom: 16, fontSize: 16, fontWeight: 700 }}>{t('admin.pdfSettings')}</h3>

          {pdfSettingsLoading ? (
            <p>{t('auth.loading')}</p>
          ) : (
            <>
              <div className="form-group">
                <label>{t('admin.pdfLogo')}</label>
                <PhotoDropzone
                  label={t('admin.pdfLogo')}
                  hint={t('admin.pdfLogoHint')}
                  accept="image/png,image/jpeg,image/svg+xml"
                  multiple={false}
                  onFiles={handleLogoUpload}
                />
                {logoError && (
                  <small style={{ color: 'var(--color-danger)', display: 'block', marginTop: 4 }}>
                    {logoError}
                  </small>
                )}
                {pdfSettings.pdf_logo_filename && (
                  <small style={{ color: 'var(--color-text-light)', display: 'block', marginTop: 4 }}>
                    {t('admin.pdfCurrentLogo')}: {pdfSettings.pdf_logo_filename}
                  </small>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div className="form-group">
                  <label>{t('admin.companyName')}</label>
                  <input
                    className="form-control"
                    value={pdfSettings.company_name || ''}
                    onChange={(e) => handlePdfSettingsChange('company_name', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>{t('admin.pdfPrimaryColor')}</label>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input
                      type="color"
                      value={pdfSettings.pdf_primary_color || '#1a3c6e'}
                      onChange={(e) => handlePdfSettingsChange('pdf_primary_color', e.target.value)}
                      style={{ width: 40, height: 32, border: 'none', cursor: 'pointer' }}
                    />
                    <input
                      className="form-control"
                      value={pdfSettings.pdf_primary_color || '#1a3c6e'}
                      onChange={(e) => handlePdfSettingsChange('pdf_primary_color', e.target.value)}
                      style={{ width: 120 }}
                    />
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label>{t('admin.companyAddress')}</label>
                <input
                  className="form-control"
                  value={pdfSettings.company_address || ''}
                  onChange={(e) => handlePdfSettingsChange('company_address', e.target.value)}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div className="form-group">
                  <label>{t('admin.companyPhone')}</label>
                  <input
                    className="form-control"
                    value={pdfSettings.company_phone || ''}
                    onChange={(e) => handlePdfSettingsChange('company_phone', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>{t('admin.companyEmail')}</label>
                  <input
                    className="form-control"
                    value={pdfSettings.company_email || ''}
                    onChange={(e) => handlePdfSettingsChange('company_email', e.target.value)}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>{t('admin.companyWebsite')}</label>
                <input
                  className="form-control"
                  value={pdfSettings.company_website || ''}
                  onChange={(e) => handlePdfSettingsChange('company_website', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>{t('admin.pdfHeaderText')}</label>
                <textarea
                  className="form-control"
                  value={pdfSettings.pdf_header_text || ''}
                  onChange={(e) => handlePdfSettingsChange('pdf_header_text', e.target.value)}
                  placeholder={t('admin.pdfHeaderTextPlaceholder')}
                  rows={2}
                />
              </div>

              <div className="form-group">
                <label>{t('admin.pdfFooterText')}</label>
                <textarea
                  className="form-control"
                  value={pdfSettings.pdf_footer_text || ''}
                  onChange={(e) => handlePdfSettingsChange('pdf_footer_text', e.target.value)}
                  placeholder={t('admin.pdfFooterTextPlaceholder')}
                  rows={2}
                />
              </div>

              <div className="form-group" style={{ borderTop: '1px solid var(--color-border)', paddingTop: 16 }}>
                <h4 style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>{t('admin.termsTitle')}</h4>
                <small style={{ color: 'var(--color-text-light)', display: 'block', marginBottom: 8 }}>
                  {t('admin.termsHint')}
                </small>
                <PhotoDropzone
                  label={t('admin.termsDrop')}
                  hint={t('admin.termsDropHint')}
                  accept="*"
                  multiple={false}
                  onFiles={handleTermsUpload}
                />
                {termsError && (
                  <small role="alert" style={{ color: 'var(--color-danger)', display: 'block', marginTop: 4 }}>
                    {termsError}
                  </small>
                )}
                {termsNotice && (
                  <small role="status" style={{ color: 'var(--color-success)', display: 'block', marginTop: 4 }}>
                    {termsNotice}
                  </small>
                )}
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
                  <small style={{ color: 'var(--color-text-light)' }}>
                    {pdfSettings.terms_pdf_filename
                      ? t('admin.termsCurrent', {
                        filename: pdfSettings.terms_pdf_filename,
                        version: pdfSettings.terms_version,
                        date: pdfSettings.terms_uploaded_at ? new Date(pdfSettings.terms_uploaded_at).toLocaleDateString() : '—',
                        interpolation: { escapeValue: false },
                      })
                      : t('admin.termsNone')}
                  </small>
                  {pdfSettings.terms_pdf_filename && (
                    <button type="button" className="btn btn-secondary btn-sm" onClick={handleTermsView}>
                      {t('admin.termsView')}
                    </button>
                  )}
                </div>
              </div>

              <div className="form-group" style={{ borderTop: '1px solid var(--color-border)', paddingTop: 16 }}>
                <h4 style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>{t('admin.emailsTitle')}</h4>
                <label className="send-check" style={{ marginTop: 12 }}>
                  <input
                    type="checkbox"
                    checked={pdfSettings.appointment_confirmation_auto === 'true'}
                    onChange={(e) => handlePdfSettingsChange('appointment_confirmation_auto', e.target.checked ? 'true' : 'false')}
                  />
                  {t('admin.confirmationAuto')}
                </label>
                <small style={{ color: 'var(--color-text-light)', display: 'block' }}>{t('admin.confirmationAutoHint')}</small>

                <ArrivalWindowSettings settings={pdfSettings} onChange={handlePdfSettingsChange} />

                <label className="send-check" style={{ marginTop: 12 }}>
                  <input
                    type="checkbox"
                    checked={pdfSettings.email_bcc_enabled === 'true'}
                    onChange={(e) => handlePdfSettingsChange('email_bcc_enabled', e.target.checked ? 'true' : 'false')}
                  />
                  {t('admin.emailBccEnabled')}
                </label>
                <small style={{ color: 'var(--color-text-light)', display: 'block' }}>{t('admin.emailBccHint')}</small>
                {pdfSettings.email_bcc_enabled === 'true' && (
                  <div style={{ marginTop: 8, maxWidth: 360 }}>
                    <label htmlFor="email-bcc-address">{t('admin.emailBccAddress')}</label>
                    <input
                      id="email-bcc-address"
                      className="form-control"
                      type="email"
                      inputMode="email"
                      autoComplete="off"
                      value={pdfSettings.email_bcc_address || ''}
                      onChange={(e) => handlePdfSettingsChange('email_bcc_address', e.target.value)}
                      aria-describedby="email-bcc-address-hint"
                    />
                    <small id="email-bcc-address-hint" style={{ color: 'var(--color-text-light)', display: 'block', marginTop: 4 }}>
                      {t('admin.emailBccAddressHint')}
                    </small>
                  </div>
                )}
                {bccError && (
                  <small role="alert" style={{ color: 'var(--color-danger)', display: 'block', marginTop: 4 }}>
                    {bccError}
                  </small>
                )}
              </div>

              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 16 }}>
                <button
                  className="btn btn-primary"
                  onClick={handleSavePdfSettings}
                  disabled={pdfSaving}
                >
                  {pdfSaving ? t('auth.loading') : t('wizard.save')}
                </button>
                {pdfSaved && (
                  <span style={{ color: 'var(--color-success)', fontSize: 13, fontWeight: 500 }}>
                    {t('wizard.saved')}
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* InvoiceNinja Tab */}
      {activeTab === 'invoiceninja' && (
        <div className="card" style={{ marginTop: 0 }}>
          <h3 style={{ marginBottom: 8, fontSize: 16, fontWeight: 700 }}>{t('admin.invoiceNinja')}</h3>
          <p style={{ fontSize: 13, color: 'var(--color-text-light)', marginBottom: 16 }}>{t('admin.inIntro')}</p>
          {pdfSettingsLoading ? (
            <p>{t('auth.loading')}</p>
          ) : (
            <>
              <div className="form-group">
                <label>{t('admin.inUrl')}</label>
                <input className="form-control" value={pdfSettings.invoiceninja_base_url || ''}
                  onChange={(e) => handlePdfSettingsChange('invoiceninja_base_url', e.target.value)}
                  placeholder={t('admin.inUrlPlaceholder')} />
              </div>
              <div className="form-group">
                <label>{t('admin.inToken')}</label>
                <input className="form-control" type="password" value={inToken} autoComplete="new-password"
                  onChange={(e) => setInToken(e.target.value)} placeholder={t('admin.inTokenPlaceholder')} />
                {pdfSettings.invoiceninja_api_token_set && !inToken && (
                  <small style={{ color: 'var(--color-success)', display: 'block', marginTop: 4 }}>
                    {t('admin.inTokenConfigured')}
                  </small>
                )}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div className="form-group">
                  <label>{t('admin.inCurrencyId')}</label>
                  <input className="form-control" value={pdfSettings.invoiceninja_currency_id || ''}
                    onChange={(e) => handlePdfSettingsChange('invoiceninja_currency_id', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>{t('admin.inCountryId')}</label>
                  <input className="form-control" value={pdfSettings.invoiceninja_country_id || '250'}
                    onChange={(e) => handlePdfSettingsChange('invoiceninja_country_id', e.target.value)} />
                </div>
              </div>
              <small style={{ color: 'var(--color-text-light)', fontSize: 12, display: 'block', marginTop: -8, marginBottom: 12 }}>
                {t('admin.inIdsHelp')}
              </small>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                <div className="form-group">
                  <label>{t('admin.inDefaultTaxRate')}</label>
                  <input className="form-control" value={pdfSettings.invoiceninja_default_tax_rate || '20'}
                    onChange={(e) => handlePdfSettingsChange('invoiceninja_default_tax_rate', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>{t('admin.inDefaultTaxName')}</label>
                  <input className="form-control" value={pdfSettings.invoiceninja_default_tax_name || 'TVA'}
                    onChange={(e) => handlePdfSettingsChange('invoiceninja_default_tax_name', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>{t('admin.inTimezone')}</label>
                  <input className="form-control" value={pdfSettings.invoiceninja_timezone || 'Europe/Paris'}
                    onChange={(e) => handlePdfSettingsChange('invoiceninja_timezone', e.target.value)} />
                </div>
              </div>
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 500 }}>
                  <input type="checkbox" checked={String(pdfSettings.invoiceninja_auto_send) === 'true'}
                    onChange={(e) => handlePdfSettingsChange('invoiceninja_auto_send', e.target.checked ? 'true' : 'false')} />
                  {t('admin.inAutoSend')}
                </label>
              </div>
              <div className="form-group" style={{ maxWidth: 260 }}>
                <label htmlFor="in-due-days">{t('admin.inDueDays')}</label>
                <input id="in-due-days" className="form-control" type="number" min="0" max="365" step="1"
                  value={pdfSettings.invoiceninja_due_days ?? '7'}
                  onChange={(e) => handlePdfSettingsChange('invoiceninja_due_days', e.target.value)} />
                <p style={{ fontSize: 12, color: 'var(--color-text-light)', margin: '4px 0 0' }}>{t('admin.inDueDaysHint')}</p>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 16, flexWrap: 'wrap' }}>
                <button className="btn btn-primary" onClick={handleSaveInvoiceNinja} disabled={inSaving}>
                  {inSaving ? t('auth.loading') : t('wizard.save')}
                </button>
                <button className="btn btn-secondary" onClick={handleTestConnection} disabled={inTesting}>
                  {inTesting ? t('admin.inTesting') : t('admin.inTest')}
                </button>
                {inSaved && (
                  <span style={{ color: 'var(--color-success)', fontSize: 13, fontWeight: 500 }}>{t('wizard.saved')}</span>
                )}
                {inTestResult && (
                  <span style={{ fontSize: 13, fontWeight: 600, color: inTestResult.ok ? 'var(--color-success)' : 'var(--color-danger)' }}>
                    {inTestResult.ok
                      ? t('admin.inTestOk')
                      : `${t('admin.inTestFail')} — ${t('admin.inReason_' + inTestResult.reason, inTestResult.reason)}`}
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Billing Presets Tab */}
      {activeTab === 'presets' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
            <p style={{ fontSize: 13, color: 'var(--color-text-light)', margin: 0 }}>{t('admin.presetsIntro')}</p>
            <button className="btn btn-primary" onClick={() => { resetPresetForm(); setShowPresetForm(true); }}>
              + {t('admin.addPreset')}
            </button>
          </div>

          {showPresetForm && (
            <div className="card" style={{ marginBottom: 24 }}>
              <h3 style={{ marginBottom: 16, fontSize: 16, fontWeight: 700 }}>
                {editingPresetId ? t('admin.editPreset') : t('admin.addPreset')}
              </h3>
              <form onSubmit={handlePresetSubmit}>
                <div className="form-group">
                  <label htmlFor="preset-label">{t('admin.presetLabel')} *</label>
                  <input id="preset-label" className="form-control" value={presetForm.label}
                    onChange={(e) => setPresetForm({ ...presetForm, label: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>{t('admin.presetDescription')}</label>
                  <textarea className="form-control" rows={2} value={presetForm.description}
                    onChange={(e) => setPresetForm({ ...presetForm, description: e.target.value })} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                  <div className="form-group">
                    <label htmlFor="preset-price">{t('admin.presetPrice')}</label>
                    {/* Text + decimal keypad, not type=number: Safari refused "0,50"
                        and blocked the submit without a visible reason. */}
                    <input id="preset-price" className="form-control" type="text" inputMode="decimal" autoComplete="off"
                      value={presetForm.default_price} placeholder="0,00"
                      onChange={(e) => setPresetForm({ ...presetForm, default_price: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>{t('admin.presetTaxRate')}</label>
                    <select className="form-control" value={String(presetForm.default_tax_rate)}
                      onChange={(e) => setPresetForm({ ...presetForm, default_tax_rate: e.target.value })}>
                      {!['21', '6'].includes(String(presetForm.default_tax_rate)) && (
                        <option value={String(presetForm.default_tax_rate)}>{presetForm.default_tax_rate} %</option>
                      )}
                      <option value="21">{t('invoices.vat21')}</option>
                      <option value="6">{t('invoices.vat6')}</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>{t('admin.presetTaxName')}</label>
                    <input className="form-control" value={presetForm.default_tax_name}
                      onChange={(e) => setPresetForm({ ...presetForm, default_tax_name: e.target.value })} />
                  </div>
                </div>
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 500 }}>
                    <input type="checkbox" checked={presetForm.active}
                      onChange={(e) => setPresetForm({ ...presetForm, active: e.target.checked })} />
                    {t('admin.presetActive')}
                  </label>
                </div>
                {presetError && <p style={{ color: 'var(--color-danger)', fontSize: 13, fontWeight: 500 }}>{presetError}</p>}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-primary" type="submit">
                    {editingPresetId ? t('wizard.save') : t('admin.addPreset')}
                  </button>
                  <button className="btn btn-secondary" type="button" onClick={resetPresetForm}>{t('wizard.cancel')}</button>
                </div>
              </form>
            </div>
          )}

          {presetError && !showPresetForm && (
            <p className="admin-error" role="alert">{presetError}</p>
          )}
          {presetsLoading ? (
            <p>{t('auth.loading')}</p>
          ) : presets.length === 0 ? (
            <div className="card empty-state">
              <div className="empty-state-icon">&#128176;</div>
              <p>{t('admin.noPresets')}</p>
            </div>
          ) : (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto' }}>
                <table className="data-table stack-mobile">
                  <thead>
                    <tr>
                      <th>{t('admin.presetLabel')}</th>
                      <th>{t('admin.presetPrice')}</th>
                      <th>{t('admin.presetTaxRate')}</th>
                      <th>{t('admin.presetActive')}</th>
                      <th>{t('admin.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {presets.map((p) => (
                      <tr key={p.id}>
                        <td style={{ fontWeight: 600 }}>
                          {p.label}
                          {p.description && (
                            <div style={{ fontSize: 12, color: 'var(--color-text-light)', fontWeight: 400 }}>{p.description}</div>
                          )}
                        </td>
                        <td data-label={t('admin.presetPrice')}>{Number(p.default_price).toFixed(2).replace('.', ',')} €</td>
                        <td data-label={t('admin.presetTaxRate')}>{Number(p.default_tax_rate).toFixed(0)} %</td>
                        <td data-label={t('admin.presetActive')}>
                          <span className={`badge ${p.active ? 'badge-completed' : 'badge-draft'}`}>
                            {p.active ? t('admin.active') : t('admin.inactive')}
                          </span>
                        </td>
                        <td className="cell-actions">
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            <button className="btn btn-secondary btn-sm" onClick={() => handleEditPreset(p)}>{t('admin.edit')}</button>
                            <button className="btn btn-ghost btn-sm" onClick={() => handleTogglePreset(p)}>
                              {p.active ? t('admin.presetDeactivate') : t('admin.presetActivate')}
                            </button>
                            <button className="btn btn-danger btn-sm" onClick={() => handleDeletePreset(p.id)}>{t('admin.deleteUser')}</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* AI Tab */}
      {activeTab === 'ai' && (
        <div className="card" style={{ marginTop: 0 }}>
          <h3 style={{ marginBottom: 8, fontSize: 16, fontWeight: 700 }}>{t('admin.ai')}</h3>
          <p style={{ fontSize: 13, color: 'var(--color-text-light)', marginBottom: 16 }}>{t('admin.aiIntro')}</p>
          {pdfSettingsLoading ? (
            <p>{t('auth.loading')}</p>
          ) : (
            <>
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 500 }}>
                  <input type="checkbox" checked={String(pdfSettings.ai_enabled) === 'true'}
                    onChange={(e) => handlePdfSettingsChange('ai_enabled', e.target.checked ? 'true' : 'false')} />
                  {t('admin.aiEnabled')}
                </label>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div className="form-group">
                  <label>{t('admin.aiProvider')}</label>
                  <select className="form-control" value={pdfSettings.ai_provider || 'gemini'}
                    onChange={(e) => handlePdfSettingsChange('ai_provider', e.target.value)}>
                    <option value="gemini">{t('admin.aiProviderGemini')}</option>
                    <option value="openai">{t('admin.aiProviderOpenai')}</option>
                    <option value="anthropic">{t('admin.aiProviderAnthropic')}</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>{t(`admin.${AI_KEY_LABEL[pdfSettings.ai_provider || 'gemini']}`)}</label>
                  <input className="form-control" type="password" autoComplete="new-password"
                    value={aiKeys[pdfSettings.ai_provider || 'gemini']}
                    onChange={(e) => setAiKeys({ ...aiKeys, [pdfSettings.ai_provider || 'gemini']: e.target.value })}
                    placeholder={t('admin.aiKeyPlaceholder')} />
                  {pdfSettings[`ai_${pdfSettings.ai_provider || 'gemini'}_api_key_set`] && !aiKeys[pdfSettings.ai_provider || 'gemini'] && (
                    <small style={{ color: 'var(--color-success)', display: 'block', marginTop: 4 }}>{t('admin.aiKeyConfigured')}</small>
                  )}
                </div>
              </div>

              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-light)', margin: '4px 0 8px' }}>
                {t('admin.aiModels')}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                {[
                  ['ai_model_economy', 'aiModelEconomy'],
                  ['ai_model_standard', 'aiModelStandard'],
                  ['ai_model_vision_fast', 'aiModelVisionFast'],
                  ['ai_model_vision_premium', 'aiModelVisionPremium'],
                ].map(([key, label]) => (
                  <div className="form-group" key={key}>
                    <label>{t(`admin.${label}`)}</label>
                    <input className="form-control" value={pdfSettings[key] || ''}
                      onChange={(e) => handlePdfSettingsChange(key, e.target.value)} />
                  </div>
                ))}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                <div className="form-group">
                  <label>{t('admin.aiDefaultLevel')}</label>
                  <select className="form-control" value={pdfSettings.ai_default_level || '1'}
                    onChange={(e) => handlePdfSettingsChange('ai_default_level', e.target.value)}>
                    {[1, 2, 3, 4].map((l) => <option key={l} value={String(l)}>{l}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>{t('admin.aiMaxLevel')}</label>
                  <select className="form-control" value={pdfSettings.ai_max_level || '4'}
                    onChange={(e) => handlePdfSettingsChange('ai_max_level', e.target.value)}>
                    {[1, 2, 3, 4].map((l) => <option key={l} value={String(l)}>{l}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>{t('admin.aiTimeout')}</label>
                  <input className="form-control" type="number" value={pdfSettings.ai_timeout || '60'}
                    onChange={(e) => handlePdfSettingsChange('ai_timeout', e.target.value)} />
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 16, flexWrap: 'wrap' }}>
                <button className="btn btn-primary" onClick={handleSaveAi} disabled={aiSaving}>
                  {aiSaving ? t('auth.loading') : t('wizard.save')}
                </button>
                <button className="btn btn-secondary" onClick={handleTestAi} disabled={aiTesting}>
                  {aiTesting ? t('admin.aiTesting') : t('admin.aiTest')}
                </button>
                <button className="btn btn-secondary" onClick={handleListAiModels} disabled={aiModelsLoading}>
                  {aiModelsLoading ? t('auth.loading') : t('admin.aiListModels')}
                </button>
                {aiSaved && (
                  <span style={{ color: 'var(--color-success)', fontSize: 13, fontWeight: 500 }}>{t('wizard.saved')}</span>
                )}
                {aiTestResult && (
                  <span style={{ fontSize: 13, fontWeight: 600, color: aiTestResult.ok ? 'var(--color-success)' : 'var(--color-danger)' }}>
                    {aiTestResult.ok
                      ? t('admin.aiTestOk')
                      : `${t('admin.aiTestFail')} — ${t('admin.aiReason_' + aiTestResult.reason, aiTestResult.reason)}`}
                  </span>
                )}
              </div>

              {/* The provider's own words: a reason code alone hides whether a
                  failure is a real quota or just an unusable model name. */}
              {aiTestResult && !aiTestResult.ok && aiTestResult.detail && (
                <div style={{
                  marginTop: 10, padding: '10px 12px', fontSize: 12,
                  fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  background: 'var(--color-danger-bg)', border: '1px solid var(--color-danger)',
                  borderRadius: 'var(--radius)', color: 'var(--color-text)',
                }}>
                  {aiTestResult.model && (
                    <div style={{ marginBottom: 6, fontWeight: 700 }}>
                      {t('admin.aiTestedModel')} {aiTestResult.model}
                    </div>
                  )}
                  {aiTestResult.detail}
                </div>
              )}

              {aiModels && (
                <div style={{ marginTop: 10, fontSize: 13 }}>
                  {aiModels.ok ? (
                    <>
                      <div style={{ fontWeight: 700, marginBottom: 6 }}>
                        {t('admin.aiAvailableModels', { count: aiModels.models.length })}
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {aiModels.models.map((m) => (
                          <code key={m} style={{
                            fontSize: 11, padding: '3px 8px', borderRadius: 'var(--radius-sm)',
                            background: 'var(--color-primary-bg)', color: 'var(--color-primary)',
                          }}>{m}</code>
                        ))}
                      </div>
                      <p style={{ fontSize: 11, color: 'var(--color-text-light)', marginTop: 8 }}>
                        {t('admin.aiModelsHint')}
                      </p>
                    </>
                  ) : (
                    <span style={{ color: 'var(--color-danger)' }}>
                      {t('admin.aiTestFail')} — {t('admin.aiReason_' + aiModels.reason, aiModels.reason)}
                      {aiModels.detail ? ` · ${aiModels.detail}` : ''}
                    </span>
                  )}
                </div>
              )}

              {/* What was actually consumed (tokens billed per call) */}
              <AiUsagePanel refreshKey={aiEstTick} />

              {/* Cost estimator */}
              <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--color-border)' }}>
                <h4 style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 700 }}>{t('admin.aiCostTitle')}</h4>
                <p style={{ fontSize: 12, color: 'var(--color-text-light)', margin: '0 0 12px' }}>{t('admin.aiCostIntro')}</p>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 12 }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>{t('admin.aiCostChars')}</label>
                    <input className="form-control" type="number" min="0" step="500" value={aiEstChars}
                      onChange={(e) => setAiEstChars(Math.max(0, parseInt(e.target.value, 10) || 0))} />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>{t('admin.aiCostImages')}</label>
                    <input className="form-control" type="number" min="0" max="12" value={aiEstImages}
                      onChange={(e) => setAiEstImages(Math.max(0, Math.min(12, parseInt(e.target.value, 10) || 0)))} />
                  </div>
                </div>

                {aiEstimate ? (
                  <>
                    <div style={{ overflowX: 'auto' }}>
                      <table className="ai-cost-table">
                        <thead>
                          <tr>
                            <th>{t('admin.aiCostLevel')}</th>
                            <th>{t('admin.aiCostModel')}</th>
                            <th style={{ textAlign: 'right' }}>{t('admin.aiCostTokens')}</th>
                            <th style={{ textAlign: 'right' }}>{t('admin.aiCostPerCall')}</th>
                            <th style={{ textAlign: 'right' }}>{t('admin.aiCostPer100')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {aiEstimate.levels.map((lv) => (
                            <tr key={lv.level}>
                              <td>{t(`ai.level_${lv.level}`)}</td>
                              <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{lv.model}{lv.images ? ' 🖼️' : ''}</td>
                              <td style={{ textAlign: 'right' }}>{lv.input_tokens.toLocaleString()} / {lv.output_tokens.toLocaleString()}</td>
                              <td style={{ textAlign: 'right', fontWeight: 600 }}>${lv.usd_per_call.toFixed(4)}</td>
                              <td style={{ textAlign: 'right' }}>${lv.usd_per_100.toFixed(2)}</td>
                            </tr>
                          ))}
                          <tr>
                            <td>{t('ai.enrich')}</td>
                            <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{aiEstimate.enrich.model}</td>
                            <td style={{ textAlign: 'right' }}>{aiEstimate.enrich.input_tokens.toLocaleString()} / {aiEstimate.enrich.output_tokens.toLocaleString()}</td>
                            <td style={{ textAlign: 'right', fontWeight: 600 }}>${aiEstimate.enrich.usd_per_call.toFixed(4)}</td>
                            <td style={{ textAlign: 'right' }}>—</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <p style={{ fontSize: 11, color: 'var(--color-text-light)', marginTop: 8 }}>
                      <span className={`badge ${aiEstimate.prices_verified ? 'badge-completed' : 'badge-draft'}`} style={{ marginRight: 6 }}>
                        {aiEstimate.prices_verified ? t('admin.aiCostVerified') : t('admin.aiCostEstimated')}
                      </span>
                      {t('admin.aiCostDisclaimer', { each: aiEstimate.assumptions.image_tokens_each })}
                    </p>

                    <details style={{ marginTop: 8 }}>
                      <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>{t('admin.aiPricing')}</summary>
                      <p style={{ fontSize: 11, color: 'var(--color-text-light)', margin: '6px 0 10px' }}>{t('admin.aiPricingHint')}</p>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        {AI_PRICE_TIERS.map(([tier, label]) => (
                          <div key={tier} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                            <div className="form-group" style={{ margin: 0 }}>
                              <label style={{ fontSize: 12 }}>{t(`admin.${label}`)} — {t('admin.aiPriceIn')}</label>
                              <input className="form-control" type="number" min="0" step="0.01"
                                value={pdfSettings[`ai_price_${tier}_in`] || ''}
                                onChange={(e) => handlePdfSettingsChange(`ai_price_${tier}_in`, e.target.value)} />
                            </div>
                            <div className="form-group" style={{ margin: 0 }}>
                              <label style={{ fontSize: 12 }}>{t('admin.aiPriceOut')}</label>
                              <input className="form-control" type="number" min="0" step="0.01"
                                value={pdfSettings[`ai_price_${tier}_out`] || ''}
                                onChange={(e) => handlePdfSettingsChange(`ai_price_${tier}_out`, e.target.value)} />
                            </div>
                          </div>
                        ))}
                      </div>
                      <p style={{ fontSize: 11, color: 'var(--color-text-light)', marginTop: 8 }}>{t('admin.aiPricingSave')}</p>
                    </details>
                  </>
                ) : (
                  <p style={{ fontSize: 13, color: 'var(--color-text-light)' }}>{t('auth.loading')}</p>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {activeTab === 'gdpr' && <GdprPanel />}
    </div>
  );
}
